import {
  createContext, useCallback, useContext, useEffect, useMemo, useState,
  type ReactNode,
} from 'react'

/**
 * ClickPay v2 theming — light/dark via a `.dark` class on <html>.
 *
 * - Preference persists in localStorage under `adbetter.theme`
 *   ('light' | 'dark' | 'system'); default is 'system'.
 * - index.html runs a tiny inline script BEFORE first paint that applies the
 *   same resolution logic, so there is never a flash of the wrong theme.
 *   Keep that script in sync with resolve() below.
 * - All colors flow from CSS custom properties (src/index.css), so toggling
 *   the class re-themes everything — components never branch on the theme.
 */

export type ThemePreference = 'light' | 'dark' | 'system'
export type ResolvedTheme = 'light' | 'dark'

const STORAGE_KEY = 'adbetter.theme'

/** The header toggle's next step: Light, then Dark, then System (follow the
 *  device), then Light again (PR 12, D7). Pure, so the cycle is tested. */
// eslint-disable-next-line react-refresh/only-export-components -- pure helper next to the provider it serves; App.header.test.ts pins it
export function nextThemePreference(pref: ThemePreference): ThemePreference {
  return pref === 'light' ? 'dark' : pref === 'dark' ? 'system' : 'light'
}

function loadPreference(): ThemePreference {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (raw === 'light' || raw === 'dark' || raw === 'system') return raw
  } catch { /* ignore */ }
  return 'system'
}

function systemTheme(): ResolvedTheme {
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

function resolve(pref: ThemePreference): ResolvedTheme {
  return pref === 'system' ? systemTheme() : pref
}

function applyToDocument(resolved: ResolvedTheme): void {
  document.documentElement.classList.toggle('dark', resolved === 'dark')
}

type ThemeContextValue = {
  /** Stored preference (may be 'system'). */
  preference: ThemePreference
  /** What is actually applied right now. */
  resolved: ResolvedTheme
  setPreference: (next: ThemePreference) => void
  /** The header toggle: cycle Light, Dark, System (`nextThemePreference`). */
  toggle: () => void
}

const ThemeContext = createContext<ThemeContextValue | null>(null)

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [preference, setPreferenceState] = useState<ThemePreference>(() => loadPreference())
  const [resolved, setResolved] = useState<ResolvedTheme>(() => resolve(loadPreference()))

  // Apply on mount + whenever preference changes.
  useEffect(() => {
    const next = resolve(preference)
    setResolved(next)
    applyToDocument(next)
  }, [preference])

  // Track OS theme changes while in 'system' mode.
  useEffect(() => {
    if (preference !== 'system') return
    const mq = window.matchMedia('(prefers-color-scheme: dark)')
    const onChange = () => {
      const next = systemTheme()
      setResolved(next)
      applyToDocument(next)
    }
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  }, [preference])

  const setPreference = useCallback((next: ThemePreference) => {
    setPreferenceState(next)
    try { window.localStorage.setItem(STORAGE_KEY, next) } catch { /* ignore */ }
  }, [])

  const toggle = useCallback(() => {
    setPreference(nextThemePreference(loadPreference()))
  }, [setPreference])

  const value = useMemo(
    () => ({ preference, resolved, setPreference, toggle }),
    [preference, resolved, setPreference, toggle],
  )

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}

// eslint-disable-next-line react-refresh/only-export-components -- context hook lives with its provider by design (same pattern as lib/auth.tsx useAuth)
export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext)
  if (!ctx) throw new Error('useTheme must be used inside <ThemeProvider>')
  return ctx
}
